import type { Item } from '@/common/api/types/items';
import type { ItemDetailsSchema, ItemDetailsTabAdapter } from '../types';

const ITEM_DETAILS_FIELD_ID_MAP: Record<keyof ItemDetailsSchema, string | null> = {
  name: null,
  itemTypeId: 'type',
  itemStatusId: 'status',
  parentId: 'parent',
  description: 'description',
  teamId: 'team',
  earliestStartDateTime: 'earliestStartDate',
  deadlineDateTime: 'deadlineDate',
  estimationId: 'estimation',
};

export const createReadOnlyItemDetailsAdapter = ({
  item,
  hiddenFieldIds = [],
}: {
  item: Item;
  hiddenFieldIds?: readonly string[];
}): ItemDetailsTabAdapter => {
  const getFieldValue = <K extends keyof ItemDetailsSchema>(field: K): ItemDetailsSchema[K] => {
    switch (field) {
      case 'name':
        return item.name as ItemDetailsSchema[K];
      case 'itemStatusId':
        return item.itemStatusId as ItemDetailsSchema[K];
      case 'itemTypeId':
        return item.itemTypeId as ItemDetailsSchema[K];
      case 'description':
        return (item.description ?? undefined) as ItemDetailsSchema[K];
      case 'teamId':
        return item.teamId as ItemDetailsSchema[K];
      case 'earliestStartDateTime':
        return item.earliestStartDateTime as ItemDetailsSchema[K];
      case 'deadlineDateTime':
        return item.deadlineDateTime as ItemDetailsSchema[K];
      case 'estimationId':
        return item.estimationId as ItemDetailsSchema[K];
      case 'parentId':
        return item.parentId as ItemDetailsSchema[K];
      default: {
        const _exhaustiveCheck: never = field;
        return _exhaustiveCheck;
      }
    }
  };

  const updateFieldValue = async <K extends keyof ItemDetailsSchema>(
    _field: K,
    _value: ItemDetailsSchema[K],
  ): Promise<void> => {};

  const isEditable = (_field: keyof ItemDetailsSchema): boolean => false;

  const isDisabled = (_field: keyof ItemDetailsSchema): boolean => false;

  const isVisible = (field: keyof ItemDetailsSchema): boolean => {
    const fieldId = ITEM_DETAILS_FIELD_ID_MAP[field];
    if (fieldId === null) return true;
    return !hiddenFieldIds.includes(fieldId);
  };

  const getFieldSchema = (): ItemDetailsSchema => ({
    name: item.name,
    itemTypeId: item.itemTypeId,
    itemStatusId: item.itemStatusId,
    parentId: item.parentId ?? undefined,
    description: item.description ?? undefined,
    teamId: item.teamId ?? undefined,
    earliestStartDateTime: item.earliestStartDateTime ?? undefined,
    deadlineDateTime: item.deadlineDateTime ?? undefined,
    estimationId: item.estimationId ?? undefined,
  });

  return {
    getFieldValue,
    updateFieldValue,
    isEditable,
    isDisabled,
    isVisible,
    getFieldSchema,
  };
};
